import { useSnackbar } from '@components/snackbar';
import { ChevronDownFilledIcon, InfoIcon, LockedIcon } from '@sketchnote/icons';
import { Button } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { PLANRULES } from 'src/constants/plans';
import { formatFileResponse, routeToPrimaryViewURL } from 'src/features/files';
import { advancedStartNewFileData, startNewFileData } from 'src/features/files/assets/data';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

import { useLoaderStore } from '../../../loaders';
import { SelectWorkspace } from '../../../SelectWorkspace/selectWorkspace';

const { useState } = React;
export interface StartNewFileProps {
  onClose: () => void;
  fileWorkspace?: string;
  highlightFileType?: { type: string; view: string };
  requireFileName?: boolean;
  onCreateFile?: (id: string, title: string) => void;
  redirectToFile?: boolean;
}

export const StartNewFile = ({
  onClose,
  fileWorkspace,
  highlightFileType,
  requireFileName = false,
  onCreateFile,
  redirectToFile = true,
}: StartNewFileProps) => {
  const { setIsLoading } = useLoaderStore();
  const router = useRouter();
  const orgId = router?.query?.orgId as string;
  const mode = router?.query?.mode as string;
  const authData = useAuth((s) => s.userDetails);
  const [filename, setFilename] = useState<string>('');
  const [showAdvancedViews, setShowAdvancedViews] = useState(false);

  const { destinationWorkspace, setDestinationWorkspace } = useLayout();

  const isWorkspaceReadOnly = false;

  const [openSnackbar] = useSnackbar();
  const [selectFile, setSelectFile] = useState({
    type: highlightFileType?.type || '',
    view: highlightFileType?.view || '',
  });

  const handleSetFile = async (type: string, view?: string) => {
    setSelectFile({ type: type, view: view || '' });
  };

  const handleStartNewFile = async () => {
    if (selectFile.type === '') {
      // display Snackbar to select a file
      openSnackbar({
        text: 'Please select a file type',
        snackType: 'error',
        position: 'bottom-center',
      });
      return;
    }
    if (selectFile.type === 'template-store') {
      // onOpenTemplateLibrary();
      router.push(`/${mode}/${orgId}/template-store`);
    } else if (selectFile.type === 'contract') {
      router.push(`/${mode}/${orgId}/legal`);
    } else {
      setIsLoading(true);
      const payload = {
        name: '',
        title: filename,
        type: selectFile.type,
        ...(selectFile.view && { view: selectFile.view }),
        ...(destinationWorkspace.value && { workspace: destinationWorkspace.value }),
      };

      const response = await API.post('/files', { ...payload });

      if (response.data?.success) {
        setIsLoading(false);
        const currentFile = formatFileResponse(response.data.data.file);
        redirectToFile && router.push(routeToPrimaryViewURL({ currentFile, orgId, mode }));
        if (onCreateFile) {
          onCreateFile(currentFile.id, filename);
        }
        const viewCreated =
          (selectFile.view || selectFile.type).charAt(0).toUpperCase() +
          (selectFile.view || selectFile.type).slice(1);

        Analytics.track(`${viewCreated} created`, {
          type: selectFile.type,
          view: selectFile.view,
        });
        setDestinationWorkspace({ label: '', value: '' });
      }
    }
    setSelectFile({ type: '', view: '' });

    onClose();
  };

  const isFileTypeDisabled = (type?: string) => {
    switch (type) {
      case 'timesheet':
        return authData?.plan.type ? !PLANRULES.timesheetcreation[authData?.plan.type] : false;
      case 'list':
        return authData?.plan.type ? !PLANRULES.listcreation[authData?.plan.type] : false;
      case 'whiteboard':
        return authData?.plan.type ? !PLANRULES.whiteboardcreation[authData?.plan.type] : false;
      case 'gantt':
        return authData?.plan.type ? !PLANRULES.ganttcreation[authData?.plan.type] : false;
      case 'form':
        return authData?.plan.type ? !PLANRULES.formcreation[authData?.plan.type] : false;
      case 'calendar':
        return authData?.plan.type ? !PLANRULES.calendarcreation[authData?.plan.type] : false;
      case 'embed':
        return authData?.plan.type ? !PLANRULES.embedcreation[authData?.plan.type] : false;
      case 'agile':
        return authData?.plan.type ? !PLANRULES.agile[authData?.plan.type] : false;
      case 'pdf_chat':
        return authData?.plan.type ? !PLANRULES.pdf_chat[authData?.plan.type] : false;
      default:
        return false;
    }
  };

  const handleSetFileName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilename(e.target.value);
  };

  return (
    <div className="flex flex-col bg-gray-199 rounded-lg">
      <div className="mb-6 pt-22 bg-gray-199 rounded-t-lg">
        <span className="text-lg text-black font-medium block pb-16 px-32 border-b">
          Start new file in
        </span>
      </div>
      {/* Select a workspace */}
      <div className="flex flex-col px-32">
        <span className="text-gray-600 text-sm block mt-18">Select workspace</span>
        <div className="mt-15 mb-22 flex">
          <SelectWorkspace
            fileWorkspace={fileWorkspace}
            workspaceListClassName={'max-w-120 rounded-lg'}
          />
        </div>
      </div>
      {/* Choose a primary view */}
      <div className="flex-grow pt-16 border-t px-32 w-full">
        <section>
          <div className="flex item-center">
            <span className="text-gray-600 mb-4 text-sm block">Select files</span>
            <span className="width-full"></span>
          </div>
          <div className="grid grid-cols-2 gap-y-10">
            {startNewFileData.items.map((view, index) => {
              return (
                <View
                  key={`${view.type}-${index}`}
                  title={view.title}
                  icon={view.icon}
                  type={view.type}
                  view={view.view}
                  description={view.description}
                  selectType={selectFile.view || selectFile.type}
                  onClick={handleSetFile}
                  disabled={isFileTypeDisabled(view.type === 'board' ? view.view : view.type)}
                />
              );
            })}
          </div>
          <div className="flex items-center mt-8 ml-12">
            <InfoIcon className="text-gray-500" size={12} />
            <span className="text-gray-500 font-regular text-xs ml-8">
              You can add other views by clicking on the Add view button inside these files
            </span>
          </div>
        </section>
        <button
          className="flex items-center mt-16 pb-4 text-gray-600 mb-4 text-sm"
          onClick={() => setShowAdvancedViews((showAdvancedViews) => !showAdvancedViews)}
        >
          <span
            style={{
              transform: showAdvancedViews ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'all 0.5s',
            }}
          >
            <ChevronDownFilledIcon />
          </span>
          <span className="text-gray-600 font-regular text-sm ml-4">Show advanced views</span>
        </button>
        <section
          className={`pt-10 transition-max-height duration-500 ease-in-out overflow-hidden ${
            showAdvancedViews ? 'max-h-300' : 'max-h-0'
          }`}
          style={{
            transition: 'all 0.5s',
          }}
        >
          <div className="grid grid-cols-2 gap-y-10 pb-16">
            {advancedStartNewFileData.items.map((view, index) => {
              return (
                <View
                  key={`${view.type}-${index}`}
                  title={view.title}
                  icon={view.icon}
                  type={view.type}
                  // view={view?.view}
                  description={view.description}
                  selectType={selectFile.view || selectFile.type}
                  onClick={handleSetFile}
                  // disabled={isFileTypeDisabled(view.type === 'board' ? view.view : view.type)}
                  disabled={isFileTypeDisabled(view.type)}
                />
              );
            })}
          </div>
        </section>
      </div>

      <div className="px-32 py-18 border-t w-full flex rounded-b-lg justify-end items-end ">
        <Tippy
          content={'You do not have the required permission to create a new file in this workspace'}
          disabled={!isWorkspaceReadOnly}
        >
          <span>
            <Button
              onClick={handleStartNewFile}
              status="primary"
              className={classNames({
                'bg-gray-500': isWorkspaceReadOnly,
              })}
              disabled={
                (requireFileName && filename.length < 1) ||
                destinationWorkspace.value.length === 0 ||
                isWorkspaceReadOnly
              }
            >
              Create new file
            </Button>
          </span>
        </Tippy>
      </div>
    </div>
  );
};

const View = ({
  title,
  type,
  view,
  description,
  onClick,
  className,
  icon,
  selectType,
  disabled,
}: {
  title: string;
  type: string;
  description: string;
  view?: string;
  className?: string;
  onClick: (type: string, view?: string) => void;
  icon: JSX.Element;
  selectType?: string;
  disabled?: boolean;
}) => {
  return (
    <Tippy content={'This feature is locked for your plan'} disabled={!disabled}>
      <div className="relative">
        <button disabled={disabled} onClick={() => onClick(type, view)}>
          <div
            className={`flex w-300 h-50 rounded-lg p-8 text-left mr-16 border border-transparent  ${className} ${
              selectType === type || selectType === view ? ' border-blue-500' : ''
            }`}
          >
            <div className="flex items-center mb-4">
              <div
                className={`flex h-36 w-36 border text-xs bg-white rounded-lg mr-8 justify-center items-center ${
                  selectType === type || selectType === view ? 'text-blue-500' : 'text-gray-500'
                } `}
              >
                {icon}
              </div>
              <div>
                <div className="flex">
                  <span className="text-sm font-medium text-black inline-block">{title}</span>

                  {disabled ? (
                    <span>
                      <LockedIcon className="mx-4 text-xs inline-block -mt-7" />
                    </span>
                  ) : (
                    <></>
                  )}
                </div>
                <p className="text-xs text-gray-600 font-regular">{description}</p>
              </div>
            </div>
          </div>
        </button>
      </div>
    </Tippy>
  );
};
